import type { JsonObject, TimestampMillis } from './common';

export const PLAN_INTERVALS = ['day', 'week', 'month', 'year', 'lifetime'] as const;

export type PlanInterval = (typeof PLAN_INTERVALS)[number];

export const ENTITLEMENT_KEYS = [
  'projects',
  'scripts',
  'licenses',
  'machines_per_license',
  'checkpoints_per_day',
  'downloads_per_month',
  'build_minutes_per_month',
  'team_members',
] as const;

export type EntitlementKey = (typeof ENTITLEMENT_KEYS)[number];

export const CUSTOMER_STATUSES = ['draft', 'active', 'blocked', 'deleted'] as const;

export type CustomerStatus = (typeof CUSTOMER_STATUSES)[number];

export const SUBSCRIPTION_STATUSES = [
  'trialing',
  'active',
  'past_due',
  'paused',
  'cancelled',
  'expired',
] as const;

export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];

export const ORDER_STATUSES = [
  'draft',
  'open',
  'pending_payment',
  'authorized',
  'paid',
  'fulfilled',
  'cancelled',
  'void',
  'refunded',
  'partially_refunded',
  'failed',
] as const;

export type OrderStatus = (typeof ORDER_STATUSES)[number];

export const PAYMENT_STATUSES = [
  'uninitialized',
  'pending',
  'processing',
  'requires_action',
  'succeeded',
  'declined',
  'failed',
  'reversed',
  'refunded',
  'partially_refunded',
] as const;

export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

export const REFUND_STATUSES = [
  'requested',
  'approved',
  'processing',
  'succeeded',
  'rejected',
  'failed',
] as const;

export type RefundStatus = (typeof REFUND_STATUSES)[number];

export const PAYMENT_PROVIDERS = ['manual'] as const;

export type PaymentProvider = (typeof PAYMENT_PROVIDERS)[number];

export const SUPPORTED_CURRENCIES = ['USD', 'THB', 'EUR', 'GBP'] as const;

export type CurrencyCode = (typeof SUPPORTED_CURRENCIES)[number];

export const MINOR_UNIT_DIGITS: Record<CurrencyCode, number> = {
  USD: 2,
  THB: 2,
  EUR: 2,
  GBP: 2,
};

export const MAX_ORDER_LINE_ITEMS = 50;

export interface Plan {
  id: string;
  organization_id: string | null;
  key: string;
  name: string;
  description: string;
  interval: PlanInterval;
  amount_minor_units: number;
  currency: CurrencyCode;
  trial_days: number;
  is_public: boolean;
  created_at: TimestampMillis;
  updated_at: TimestampMillis;
  retired_at: TimestampMillis | null;
}

export interface PlanEntitlement {
  id: string;
  plan_id: string;
  feature_key: EntitlementKey;
  limit: number | null;
  is_unlimited: boolean;
  overage_price_minor_units: number | null;
  metadata: JsonObject;
}

export interface Customer {
  id: string;
  organization_id: string;
  external_ref: string | null;
  display_name: string;
  email: string | null;
  country: string | null;
  status: CustomerStatus;
  created_at: TimestampMillis;
  updated_at: TimestampMillis;
  notes: string;
}

export interface Subscription {
  id: string;
  organization_id: string;
  customer_id: string;
  plan_id: string;
  status: SubscriptionStatus;
  started_at: TimestampMillis;
  renews_at: TimestampMillis | null;
  ends_at: TimestampMillis | null;
  cancelled_at: TimestampMillis | null;
  usage_period_start: TimestampMillis;
  provider: PaymentProvider | null;
  provider_reference: string | null;
}

export interface OrderLineItem {
  id: string;
  order_id: string;
  kind: 'plan' | 'license_pack' | 'addon' | 'credit';
  reference_id: string | null;
  description: string;
  quantity: number;
  unit_amount_minor_units: number;
  total_amount_minor_units: number;
}

export interface Order {
  id: string;
  organization_id: string;
  customer_id: string;
  subscription_id: string | null;
  status: OrderStatus;
  payment_status: PaymentStatus;
  currency: CurrencyCode;
  subtotal_minor_units: number;
  discount_minor_units: number;
  tax_minor_units: number;
  total_minor_units: number;
  amount_paid_minor_units: number;
  amount_refunded_minor_units: number;
  payment_provider: PaymentProvider | null;
  payment_reference: string | null;
  created_at: TimestampMillis;
  updated_at: TimestampMillis;
  paid_at: TimestampMillis | null;
  cancelled_at: TimestampMillis | null;
  line_items: OrderLineItem[];
  metadata: JsonObject;
}

export interface Refund {
  id: string;
  organization_id: string;
  order_id: string;
  amount_minor_units: number;
  currency: CurrencyCode;
  status: RefundStatus;
  reason: string;
  requested_by: string;
  created_at: TimestampMillis;
  updated_at: TimestampMillis;
  settled_at: TimestampMillis | null;
  provider_reference: string | null;
}

export const ORDER_STATUS_TRANSITIONS: Record<OrderStatus, readonly OrderStatus[]> = {
  draft: ['open', 'cancelled', 'void'],
  open: ['pending_payment', 'cancelled', 'void'],
  pending_payment: ['authorized', 'paid', 'failed', 'cancelled'],
  authorized: ['paid', 'failed', 'void'],
  paid: ['fulfilled', 'refunded', 'partially_refunded'],
  fulfilled: ['refunded', 'partially_refunded'],
  cancelled: [],
  void: [],
  refunded: [],
  partially_refunded: ['refunded'],
  failed: ['pending_payment'],
};

export function isOrderStatus(value: string): value is OrderStatus {
  return (ORDER_STATUSES as readonly string[]).includes(value);
}

export function isPaymentStatus(value: string): value is PaymentStatus {
  return (PAYMENT_STATUSES as readonly string[]).includes(value);
}

export function canTransitionOrder(from: OrderStatus, to: OrderStatus): boolean {
  return ORDER_STATUS_TRANSITIONS[from].includes(to);
}

export function isPayable(status: OrderStatus): boolean {
  return (
    status === 'open' ||
    status === 'pending_payment' ||
    status === 'authorized' ||
    status === 'draft'
  );
}

export function refundableRemainder(
  order: Pick<Order, 'total_minor_units' | 'amount_refunded_minor_units' | 'status'>,
): number {
  if (
    order.status !== 'paid' &&
    order.status !== 'fulfilled' &&
    order.status !== 'partially_refunded'
  ) {
    return 0;
  }
  return Math.max(0, order.total_minor_units - order.amount_refunded_minor_units);
}

export function entitlementValue(
  entitlements: readonly PlanEntitlement[],
  feature: EntitlementKey,
): number | null {
  for (const entitlement of entitlements) {
    if (entitlement.feature_key === feature) {
      return entitlement.is_unlimited ? null : entitlement.limit;
    }
  }
  return null;
}

export function withinEntitlement(
  entitlements: readonly PlanEntitlement[],
  feature: EntitlementKey,
  used: number,
  requested: number,
): boolean {
  const limit = entitlementValue(entitlements, feature);
  if (limit === null) {
    return true;
  }
  return used + requested <= limit;
}
